import { Position, type Node, type Edge } from 'reactflow';
import { handleEdgeColor } from 'utils';
import { SecondaryNodeColor, BorderStyle, HandleType, NodeVariant } from 'types';
import {
  TargetVariableTitle,
  AntecedentTitle,
  ConsequenceTitle,
  targetVariableResultsData,
  antecedentResultsData,
  consequenceResultsData,
} from 'assets';

export const initialNodes: Node[] = [
  {
    id: 'A',
    type: NodeVariant.PRIMARY,
    position: { x: 400, y: 0 },
    data: {
      label: TargetVariableTitle.CUSTOMER_KNOWLEDGE_SHARING,
      handle: [{ position: Position.Left, type: HandleType.TARGET }],
      ...targetVariableResultsData[TargetVariableTitle.CUSTOMER_KNOWLEDGE_SHARING],
    },
  },
  {
    id: 'B1',
    type: NodeVariant.SECONDARY,
    position: { x: 0, y: 0 },
    data: {
      label: AntecedentTitle.HELPING_OTHER_CUSTOMERS,
      color: SecondaryNodeColor.BLUE,
      handle: [{ position: Position.Right, type: HandleType.SOURCE }],
      ...antecedentResultsData[AntecedentTitle.HELPING_OTHER_CUSTOMERS],
    },
  },
  {
    id: 'B2',
    type: NodeVariant.SECONDARY,
    position: { x: 0, y: 100 },
    data: {
      label: AntecedentTitle.SOCIAL_BOND,
      color: SecondaryNodeColor.GREEN,
      handle: [{ position: Position.Right, type: HandleType.SOURCE }],
      ...antecedentResultsData[AntecedentTitle.SOCIAL_BOND],
    },
  },
  {
    id: 'B3',
    type: NodeVariant.SECONDARY,
    position: { x: 0, y: 200 },
    data: {
      label: AntecedentTitle.SUBJECTIVE_HAPPINESS,
      color: SecondaryNodeColor.GREEN,
      handle: [{ position: Position.Right, type: HandleType.SOURCE }],
      ...antecedentResultsData[AntecedentTitle.SUBJECTIVE_HAPPINESS],
    },
  },
  {
    id: 'B4',
    type: NodeVariant.SECONDARY,
    position: { x: 0, y: 300 },
    data: {
      label: AntecedentTitle.RECIPROCITY,
      color: SecondaryNodeColor.GREEN,
      handle: [{ position: Position.Right, type: HandleType.SOURCE }],
      ...antecedentResultsData[AntecedentTitle.RECIPROCITY],
    },
  },
  {
    id: 'C',
    type: NodeVariant.PRIMARY,
    position: { x: 450, y: 300 },
    data: {
      label: TargetVariableTitle.SUGGESTION_SHARING_LIKELIHOOD,
      handle: [{ position: Position.Left, type: HandleType.TARGET }],
      ...targetVariableResultsData[TargetVariableTitle.SUGGESTION_SHARING_LIKELIHOOD],
    },
  },
  {
    id: 'D1',
    type: NodeVariant.SECONDARY,
    position: { x: 100, y: 400 },
    data: {
      label: AntecedentTitle.SELF_BENEFIT,
      handle: [{ position: Position.Right, type: HandleType.SOURCE }],
      color: SecondaryNodeColor.BLUE,
      ...antecedentResultsData[AntecedentTitle.SELF_BENEFIT],
    },
  },
  {
    id: 'D2',
    type: NodeVariant.SECONDARY,
    position: { x: 100, y: 500 },
    data: {
      label: AntecedentTitle.FIRM_BENEFIT,
      handle: [{ position: Position.Right, type: HandleType.SOURCE }],
      color: SecondaryNodeColor.BLUE,
      ...antecedentResultsData[AntecedentTitle.FIRM_BENEFIT],
    },
  },
  {
    id: 'D3',
    type: NodeVariant.SECONDARY,
    position: { x: 100, y: 600 },
    data: {
      label: AntecedentTitle.OTHER_BENEFIT,
      handle: [{ position: Position.Right, type: HandleType.SOURCE }],
      color: SecondaryNodeColor.GREEN,
      ...antecedentResultsData[AntecedentTitle.OTHER_BENEFIT],
    },
  },
  {
    id: 'E',
    type: NodeVariant.PRIMARY,
    position: { x: 450, y: 650 },
    data: {
      label: TargetVariableTitle.CUSTOMER_FEEDBACK,
      handle: [
        { position: Position.Left, type: HandleType.TARGET },
        { position: Position.Right, type: HandleType.SOURCE },
      ],
      ...targetVariableResultsData[TargetVariableTitle.CUSTOMER_FEEDBACK],
    },
  },
  {
    id: 'F1',
    type: NodeVariant.SECONDARY,
    position: { x: 0, y: 700 },
    data: {
      label: AntecedentTitle.COMPETITION,
      handle: [{ position: Position.Right, type: HandleType.SOURCE }],
      color: SecondaryNodeColor.RED,
      ...antecedentResultsData[AntecedentTitle.COMPETITION],
    },
  },
  {
    id: 'F2',
    type: NodeVariant.SECONDARY,
    position: { x: 0, y: 800 },
    data: {
      label: AntecedentTitle.EXPORTS_DESTINATION,
      handle: [{ position: Position.Right, type: HandleType.SOURCE }],
      color: SecondaryNodeColor.GREEN,
      ...antecedentResultsData[AntecedentTitle.EXPORTS_DESTINATION],
    },
  },
  {
    id: 'G1',
    type: NodeVariant.SECONDARY,
    position: { x: 800, y: 700 },
    data: {
      label: ConsequenceTitle.INNOVATION,
      handle: [{ position: Position.Left, type: HandleType.TARGET }],
      borderStyle: BorderStyle.DASHED,
      color: SecondaryNodeColor.GREEN,
      ...consequenceResultsData[ConsequenceTitle.INNOVATION],
    },
  },
  {
    id: 'H',
    type: NodeVariant.PRIMARY,
    position: { x: 450, y: 850 },
    data: {
      label: TargetVariableTitle.PROVIDING_FEEDBACK,
      handle: [
        { position: Position.Left, type: HandleType.TARGET },
        { position: Position.Right, type: HandleType.SOURCE },
      ],
      ...targetVariableResultsData[TargetVariableTitle.PROVIDING_FEEDBACK],
    },
  },
  {
    id: 'I1',
    type: NodeVariant.SECONDARY,
    position: { x: 100, y: 900 },
    data: {
      label: AntecedentTitle.CORPORATE_IMAGE,
      handle: [{ position: Position.Right, type: HandleType.SOURCE }],
      color: SecondaryNodeColor.GREEN,
      ...antecedentResultsData[AntecedentTitle.CORPORATE_IMAGE],
    },
  },
  {
    id: 'J1',
    type: NodeVariant.SECONDARY,
    position: { x: 800, y: 950 },
    data: {
      label: ConsequenceTitle.LONG_TERM_RELATIONSHIP_ORIENTED,
      handle: [{ position: Position.Left, type: HandleType.TARGET }],
      borderStyle: BorderStyle.DASHED,
      color: SecondaryNodeColor.BLUE,
      ...consequenceResultsData[ConsequenceTitle.LONG_TERM_RELATIONSHIP_ORIENTED],
    },
  },
];

export const initialEdges: Edge[] = [
  { id: 'B1-A', source: 'B1', target: 'A', style: handleEdgeColor(SecondaryNodeColor.BLUE) },
  { id: 'B2-A', source: 'B2', target: 'A', style: handleEdgeColor(SecondaryNodeColor.GREEN) },
  { id: 'B3-A', source: 'B3', target: 'A', style: handleEdgeColor(SecondaryNodeColor.GREEN) },
  { id: 'B4-A', source: 'B4', target: 'A', style: handleEdgeColor(SecondaryNodeColor.GREEN) },
  { id: 'D1-C', source: 'D1', target: 'C', style: handleEdgeColor(SecondaryNodeColor.BLUE) },
  { id: 'D2-C', source: 'D2', target: 'C', style: handleEdgeColor(SecondaryNodeColor.BLUE) },
  { id: 'D3-C', source: 'D3', target: 'C', style: handleEdgeColor(SecondaryNodeColor.GREEN) },
  { id: 'F1-E', source: 'F1', target: 'E', style: handleEdgeColor(SecondaryNodeColor.RED) },
  { id: 'F2-E', source: 'F2', target: 'E', style: handleEdgeColor(SecondaryNodeColor.GREEN) },
  {
    id: 'E-G1',
    source: 'E',
    target: 'G1',
    style: handleEdgeColor(SecondaryNodeColor.GREEN),
    animated: true,
  },
  { id: 'I1-H', source: 'I1', target: 'H', style: handleEdgeColor(SecondaryNodeColor.GREEN) },
  {
    id: 'H-J1',
    source: 'H',
    target: 'J1',
    style: handleEdgeColor(SecondaryNodeColor.BLUE),
    animated: true,
  },
];
